import { Link } from 'react-router-dom';
import { ArrowRight, Info } from 'lucide-react';
import { IMG, SITE } from '../data/site';
import { Seo } from '../lib/seo';
import { Reveal, SpotlightImage } from '../components/motion';
import { PageHero, CTABand } from '../components/chrome';

const crumbs = [{ label: 'Home', href: '/' }, { label: 'Manufacturers & Partners' }];

const CATEGORIES = [
  ['Analytical instrumentation manufacturers', 'GC/MS, FTIR, Raman, XRF, mass spectrometry, and chemistry platform OEMs, selected per requirement.'],
  ['Authorized scientific distributors', 'Sourcing channels for brand-specific instruments, parts, and consumables at procurement-ready pricing.'],
  ['Field service & calibration providers', 'Qualified providers for installation support, calibration, preventive maintenance, and repair.'],
  ['Environmental & waste service firms', 'Licensed partners for hazardous waste handling and site-specific environmental services.'],
];

export default function Manufacturers() {
  return (
    <div data-testid="page-manufacturers">
      <Seo
        title="Manufacturers & Partners | ToxGlobal Analytics"
        description="ToxGlobal Analytics sources analytical instrumentation through a network of OEMs, authorized distributors, and specialized service providers — coordinated under one accountable contractor."
        path="/manufacturers-partners"
        breadcrumbs={crumbs}
      />
      <PageHero
        eyebrow="Manufacturers & Partners"
        lines="A network model, accountable to one contractor"
        sub="ToxGlobal works through an established network of OEMs, authorized distributors, and specialized service providers. The model gives agencies manufacturer-supported systems with a single accountable contractor from quote through validation and ongoing support."
        breadcrumbs={crumbs}
        image={IMG.instrument}
        imageAlt="Analytical instrument manufacturer platforms in a laboratory"
      />

      <section className="section-pad">
        <div className="container-x grid lg:grid-cols-2 gap-12">
          <div>
            <Reveal>
              <h2 className="display text-2xl sm:text-3xl text-ink">How sourcing works</h2>
              <p className="mt-5 text-slate-600 leading-relaxed">
                When an agency or laboratory requests a specific manufacturer or model, ToxGlobal routes the requirement
                through the appropriate OEM or authorized distribution channel, quotes it with procurement-ready
                documentation, and coordinates installation and service through manufacturer-authorized channels where the
                platform requires it.
              </p>
            </Reveal>
            <Reveal delay={0.08}>
              <ul className="mt-8 border-t border-slate-200">
                {CATEGORIES.map(([t, d]) => (
                  <li key={t} className="py-5 border-b border-slate-200">
                    <h3 className="font-display font-semibold text-ink">{t}</h3>
                    <p className="mt-1 text-sm text-slate-600">{d}</p>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div>
            <Reveal delay={0.05}>
              <SpotlightImage src={IMG.installTech} alt="Authorized installation coordination for analytical instrumentation" ratio="aspect-[4/3]" />
            </Reveal>
            <Reveal delay={0.1}>
              <div className="border border-amber-300 bg-amber-50 p-6 mt-8 flex gap-3" data-testid="trademark-disclaimer">
                <Info size={18} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900 leading-relaxed">
                  <strong>A note on authorization.</strong> ToxGlobal is an independent supplier and does not publicly claim
                  authorized-status for any manufacturer unless documentation supports that exact relationship. Product names
                  and platforms referenced across this site are trademarks of their respective owners and are used only to
                  describe sourcing capability. Manufacturer authorization letters are provided as required during procurement.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="spotlight-dark grain text-white section-pad" data-testid="manufacturers-cta">
        <div className="container-x text-center max-w-3xl mx-auto">
          <Reveal>
            <p className="eyebrow text-signal mb-3">Request a platform</p>
            <h2 className="display text-3xl sm:text-5xl">Need a specific manufacturer or model?</h2>
            <p className="mt-4 text-slate-300">Name the platform. ToxGlobal will source it, install it, and stand behind it.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/contact" className="btn-light" data-testid="manufacturers-source-cta">Source an Instrument <ArrowRight size={15} /></Link>
              <a href={`mailto:${SITE.email}`} className="btn-outline-light" data-testid="manufacturers-email-cta">Email {SITE.email}</a>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
